"use client";

import { useState } from "react";
import { postApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";

const normalizeReason = (reason, fallback = "Unknown reason") => {
  if (typeof reason !== "string") {
    return fallback;
  }

  const normalizedReason = reason.trim();
  return normalizedReason || fallback;
};

const buildReasonSummary = (items = [], reasonKey) => {
  const reasonCounts = new Map();

  items.forEach((item) => {
    const reason = normalizeReason(item?.[reasonKey]);
    reasonCounts.set(reason, (reasonCounts.get(reason) || 0) + 1);
  });

  return Array.from(reasonCounts.entries())
    .map(([reason, count]) => ({
      reason,
      count,
    }))
    .sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }

      return a.reason.localeCompare(b.reason);
    });
};

const normalizeBulkImportResult = (result, candidatesData = [], fallback = {}) => {
  const skippedRecords =
    result?.details?.skipped?.map((record) => ({
      row: candidatesData[record.index]?._rowNumber || record.index + 2,
      index: record.index,
      name: record.name || "Unknown",
      email: record.email || "Unknown",
      reason: normalizeReason(record.reason),
    })) || [];

  const errors =
    result?.details?.errors?.map((apiError) => ({
      row: candidatesData[apiError.index]?._rowNumber || apiError.index + 2,
      index: apiError.index,
      name: apiError.candidate?.name || "Unknown",
      email: apiError.candidate?.email || "Unknown",
      error: normalizeReason(apiError.error, "Import failed"),
    })) || [];

  return {
    success: fallback.success ?? result?.success ?? false,
    created: result?.summary?.successful || fallback.created || 0,
    failed:
      result?.summary?.failed ??
      fallback.failed ??
      errors.length,
    skipped:
      result?.summary?.skipped ??
      fallback.skipped ??
      skippedRecords.length,
    errors,
    skippedRecords,
    successfulRecords: result?.successfulRecords || fallback.successfulRecords || [],
    groupedReasons: {
      skipped: buildReasonSummary(skippedRecords, "reason"),
      failed: buildReasonSummary(errors, "error"),
    },
    message: result?.message || fallback.message || "",
  };
};

export const useBulkImport = () => {
  const [isImporting, setIsImporting] = useState(false);

  const mapExperienceYearsToRange = (years) => {
    if (!Number.isFinite(years) || years < 0) {
      return "";
    }
    if (years <= 1) return "0-1";
    if (years <= 3) return "1-3";
    if (years <= 5) return "3-5";
    if (years <= 8) return "5-8";
    if (years <= 12) return "8-12";
    return "12+";
  };

  const normalizeExperienceRange = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    if (typeof value === "number") {
      return mapExperienceYearsToRange(value);
    }

    const rawValue = String(value).trim();
    if (!rawValue) {
      return "";
    }

    if (/^\d+(\.\d+)?$/.test(rawValue)) {
      return mapExperienceYearsToRange(Number.parseFloat(rawValue));
    }

    const normalizedValue = rawValue
      .toLowerCase()
      .replace(/[–—]/g, "-")
      .replace(/\byrs?\b/g, "year")
      .replace(/\byears\b/g, "year")
      .replace(/\s+/g, "");

    const supportedRanges = ["0-1", "1-3", "3-5", "5-8", "8-12", "12+"];
    const matchedRange = supportedRanges.find((range) => {
      const normalizedRange = range.replace(/\s+/g, "");
      return (
        normalizedValue === normalizedRange ||
        normalizedValue === `${normalizedRange}year`
      );
    });

    return matchedRange || "";
  };

  const bulkImportCandidates = async (candidatesData) => {
    setIsImporting(true);

    console.log("Bulk import candidates data:", candidatesData);

    try {
      // Validate and clean data before sending
      const cleanedData = candidatesData.map((candidate, index) => {
        const cleaned = { ...candidate };

        // Remove row number used for error reporting
        delete cleaned._rowNumber;

        // Set default values for required fields if missing
        if (!cleaned.resume) {
          cleaned.resume = "--no-file--";
        }
        if (!cleaned.coverLetter) {
          cleaned.coverLetter = "";
        }

        // Ensure skills is an array
        if (typeof cleaned.skills === "string") {
          cleaned.skills = cleaned.skills
            .split(",")
            .map((skill) => skill.trim())
            .filter(Boolean);
        }

        const normalizedExperienceRange = normalizeExperienceRange(
          cleaned.experienceRange ?? cleaned.experienceYears
        );
        if (normalizedExperienceRange) {
          cleaned.experienceRange = normalizedExperienceRange;
        }
        delete cleaned.experienceYears;

        return cleaned;
      });

      // Call bulk import API
      const response = await postApi(`${API_PATHS.CANDIDATE_LIST}bulk-import`, {
        candidates: cleanedData,
      });

      if (response.status === 200 || response.status === 201) {
        const result = response.data;
        console.log("Bulk import result:", result);
        return normalizeBulkImportResult(result, candidatesData, {
          success: result.summary?.failed === 0,
        });
      } else {
        throw new Error("Bulk import API returned an error");
      }
    } catch (error) {
      console.error("Bulk import error:", error);

      if (error.response && error.response.status === 400) {
        const result = error.response.data;
        return normalizeBulkImportResult(result, candidatesData, {
          success: false,
          failed: candidatesData.length,
        });
      }

      // If API call fails, create a mock error response
      return {
        success: false,
        created: 0,
        failed: candidatesData.length,
        errors: candidatesData.map((candidate, index) => ({
          row: candidate._rowNumber || index + 2,
          index,
          name: candidate.name || "Unknown",
          email: candidate.email || "Unknown",
          error: error instanceof Error ? error.message : "Import failed",
        })),
        skipped: 0,
        skippedRecords: [],
        groupedReasons: {
          skipped: [],
          failed: [
            {
              reason: error instanceof Error ? error.message : "Import failed",
              count: candidatesData.length,
            },
          ],
        },
        successfulRecords: [],
        message: error instanceof Error ? error.message : "Import failed",
      };
    } finally {
      setIsImporting(false);
    }
  };

  return {
    bulkImportCandidates,
    isImporting,
  };
};
